/**
 * NonWorkingDayWarning — "that day is a Sunday / a holiday, are you sure?"
 *
 * A warning, NOT a gate: the primary action logs the time anyway. Weekend
 * releases and holiday cover are real, so the only job here is to catch the far
 * more common case of a mistyped date before it lands in someone's timesheet.
 */
import React from "react";
import { CalendarOff } from "lucide-react";
import { T } from "../../components/em/ui";
import { nonWorkingLabel } from "./useNonWorkingDays";

// '2026-08-15' → '15 Aug 2026', formatted from the string's own parts. Going via
// `new Date()` would render the previous day in any zone behind UTC, because a
// bare date string parses as UTC midnight.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtIsoDay = (d) =>
  /^\d{4}-\d{2}-\d{2}$/.test(d || "")
    ? `${Number(d.slice(8, 10))} ${MONTHS[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`
    : d || "";

export default function NonWorkingDayWarning({
  warning,
  busy = false,
  confirmLabel = "Log anyway",
  onConfirm,
  onCancel,
}) {
  if (!warning) return null;
  return (
    <div
      // stopPropagation matters because this can be mounted INSIDE another
      // modal's backdrop (the edit-entry dialog): without it, dismissing this
      // warning would bubble up and close the dialog underneath too.
      onClick={(e) => {
        e.stopPropagation();
        onCancel();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17,24,39,0.45)",
        // Above the edit-entry modal (1000), which can raise this from inside.
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--em-bg-card)",
          borderRadius: 14,
          boxShadow: T.shadowMd,
          padding: 22,
        }}
      >
        <div style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: T.pill,
              flexShrink: 0,
              background: T.warn50,
              color: T.warn,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CalendarOff size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: `700 15px/1.3 ${T.font}`, color: T.fg1 }}>
              {warning.kind === "holiday"
                ? "That day is a holiday"
                : "That day is not a working day"}
            </div>
            <div
              style={{
                marginTop: 7,
                font: `400 13px/1.5 ${T.font}`,
                color: T.fg3,
              }}
            >
              <strong style={{ color: T.fg1 }}>{fmtIsoDay(warning.day)}</strong>{" "}
              is {nonWorkingLabel(warning)}. You can still log time on it — this
              is just a heads-up in case the date is wrong.
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 20,
          }}
        >
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: "9px 16px",
              borderRadius: T.rMd,
              border: `1px solid ${T.border}`,
              background: "var(--em-bg-card)",
              cursor: busy ? "default" : "pointer",
              font: `600 13px ${T.font}`,
              color: T.fg1,
            }}
          >
            Change the date
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            autoFocus
            style={{
              padding: "9px 18px",
              borderRadius: T.rMd,
              border: 0,
              cursor: busy ? "default" : "pointer",
              background: T.gradient,
              color: "#fff",
              font: `600 13px ${T.font}`,
              boxShadow: T.shadowBrand,
              opacity: busy ? 0.65 : 1,
            }}
          >
            {busy ? "Saving…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
